import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { WhatsAppCTA } from "./WhatsAppCTA";

const NAV_LINKS = [
  { label: "Serviços", target: "servicos" },
  { label: "Processo", target: "processo" },
  { label: "Contato", target: "contato" },
  { label: "Quem Somos", route: "/quem-somos" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (target) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: target } });
    } else {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLink = (link) => {
    if (link.route) {
      setOpen(false);
      navigate(link.route);
    } else {
      goToSection(link.target);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/[0.05]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="w-full max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-14 md:h-12">
          <button
            onClick={() => goToSection("hero")}
            data-testid="nav-logo-button"
            className="focus-visible:outline-none"
          >
            <Logo size={24} />
          </button>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => handleLink(link)}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-[13px] text-[#86868b] hover:text-[#f5f5f7] transition-colors duration-300"
              >
                {link.label}
              </button>
            ))}
            <WhatsAppCTA
              label="Falar no WhatsApp"
              testId="nav-whatsapp-cta"
              className="!px-5 !py-2 !text-[13px]"
            />
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-[#f5f5f7] p-2 -mr-2"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-black/80 backdrop-blur-xl border-b border-white/[0.05]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleLink(link)}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-left text-lg text-[#f5f5f7]"
                >
                  {link.label}
                </button>
              ))}
              <WhatsAppCTA
                label="Falar no WhatsApp"
                fullWidth
                icon={<MessageCircle size={18} />}
                testId="nav-mobile-whatsapp-cta"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
